import { Controller } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import Input from 'src/components/Input/Input';
import { SalesRoleField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/RoleDescriptionField/SalesRoleField/SalesRoleField';
import { clsx } from 'clsx';
import type { PersonaFieldProps } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/personaFieldProps';
import type { ConfigurationFormFieldsObject } from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import type { PersonaInputData } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';

export const RoleDescriptionField = ({
  field,
  salesRoleFields,
  data,
  salesRoleData,
  control,
  readonly,
  role,
  roleClickValue,
  setValue,
  watch,
  compact,
}: PersonaFieldProps<'pf_role_custom'> & {
  salesRoleFields: Pick<ConfigurationFormFieldsObject, 'pf_cta' | 'pf_icp'>;
  salesRoleData: Pick<PersonaInputData, 'pf_cta' | 'pf_icp'>;
  role: string;
  roleClickValue?: string;
}) => {
  const [isExpandNeeded, setExpandNeeded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const wrapperRef = useRef(null);
  const fieldValue = watch('pf_role_custom');

  useEffect(() => {
    if (role !== 'custom') {
      setValue('pf_role_custom', '');
      return;
    }

    if (readonly && data) {
      setValue(field.key, data);
      return;
    }
    if (fieldValue) {
      setValue(field.key, fieldValue);
      return;
    }

    if (data) {
      setValue('pf_role_custom', data);
    }
  }, [role, data]);

  useEffect(() => {
    if (wrapperRef.current) {
      const isNeeded = wrapperRef.current.clientHeight < wrapperRef.current.firstChild.offsetHeight;

      setExpandNeeded(isNeeded);
    }
  }, [wrapperRef.current]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return field ? (
    <div className={'pl-2.5 border-l border-[#a498ff80]'}>
      {Boolean(readonly && data) && (
        <div className={'relative'}>
          <div
            ref={wrapperRef}
            className={clsx(
              'overflow-hidden transition-all duration-500 ease-in-out',
              expanded ? 'max-h-auto' : 'max-h-36',
            )}
          >
            <div className={'h-[calc(100%-40px)]'}>
              <p className={'font-medium text-sm text-ebony_opacity_50 whitespace-break-spaces'}>
                {data}
              </p>
            </div>
          </div>
          {(expanded || isExpandNeeded) && (
            <button
              type="button"
              onClick={toggleExpanded}
              className={
                'text-[#7A6BE5] mt-2 font-semibold text-sm hover:underline cursor-pointer w-full text-left'
              }
            >
              {expanded ? 'Show Less...' : 'Show More...'}
            </button>
          )}
        </div>
      )}
      {Boolean(!readonly && role === 'support') && (
        <p className={'font-medium text-sm text-ebony_opacity_50'}>
          This role is optimized for resolving support tickets, troubleshooting issues, and
          delivering clear explanations.
        </p>
      )}
      {Boolean(!readonly && role === 'sales') && (
        <SalesRoleField
          data={salesRoleData}
          watch={watch}
          setValue={setValue}
          roleClickValue={roleClickValue}
        />
      )}
      {Boolean(!readonly && role === 'custom') && (
        <Controller
          control={control}
          name={field.key}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div className={'flex flex-col w-full'}>
              <Input
                readonly={readonly}
                textareaRows={compact ? 3 : 2}
                type={'textarea'}
                label={'Description*'}
                placeholder={
                  'As a customer support assistant of CoSupport AI, your goal is to answer customers’ questions and assist customers in troubleshooting any issues they report.'
                }
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
              />
            </div>
          )}
        />
      )}
    </div>
  ) : null;
};
